import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import ApiService from "../services/apiService";
import { jsPDF } from "jspdf";
import getApiErrorMessage from "../utils/getApiErrorMessage";
import "../RevenueAnalytics.css";

const formatDate = (date) => date.toISOString().slice(0, 10);
const currency = (value) => new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP", maximumFractionDigits: 2 }).format(Number(value || 0));

const parseChartDate = (value) => {
  if (!value && value !== 0) return null;

  const raw = String(value).trim();
  if (!raw) return null;

  const normalized = raw.includes(' ') ? raw.replace(' ', 'T') : raw;
  const parsed = new Date(normalized);

  if (!Number.isNaN(parsed.getTime())) return parsed;

  const [year, month, day] = raw.split(/[-/]/).map(part => Number(part));
  if (year && month && day) {
    return new Date(Date.UTC(year, month - 1, day));
  }

  return null;
};

const shortDate = (value) => {
  const parsed = parseChartDate(value);
  if (!parsed) return 'N/A';

  return parsed.toLocaleDateString("en-PH", { month: "short", day: "numeric" });
};

const formatPdfDate = (value) => {
  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  const parsed = dateOnlyMatch
    ? new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]))
    : parseChartDate(value);

  return parsed
    ? parsed.toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "numeric" })
    : "N/A";
};

const formatPdfCurrency = (value) => `PHP ${new Intl.NumberFormat("en-PH", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(Number(value || 0))}`;

export default function RevenueAnalytics() {
  const today = new Date();
  const initialTo = formatDate(today);
  const initialFromDate = new Date(today);
  initialFromDate.setDate(today.getDate() - 29);

  const [from, setFrom] = useState(formatDate(initialFromDate));
  const [to, setTo] = useState(initialTo);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadReport = async () => {
    try {
      setLoading(true);
      setError("");
      const nextReport = await ApiService.getRevenueAnalytics(from, to);
      if (!nextReport || typeof nextReport !== "object" || Array.isArray(nextReport)) {
        throw new Error("Invalid revenue analytics response.");
      }
      setReport(nextReport);
    } catch (requestError) {
      console.error("Error loading revenue analytics:", requestError);
      setError(getApiErrorMessage(requestError, "Unable to load revenue analytics."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
    // The initial report is loaded once; filters are applied with the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const daily = report?.daily || [];
  const maxRevenue = Math.max(...daily.map(item => Number(item.revenue || 0)), 1);
  const summary = report?.summary || {};

  const exportRevenueReport = () => {
    if (!report) return;

    try {
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 16;
      const contentWidth = pageWidth - margin * 2;
      const generatedAt = new Date();
      let y = 0;

      const ensureSpace = (height) => {
        if (y + height > pageHeight - 18) {
          pdf.addPage();
          y = 18;
        }
      };

      const drawSectionTitle = (title) => {
        ensureSpace(13);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(12);
        pdf.setTextColor(18, 60, 71);
        pdf.text(title, margin, y + 5);
        pdf.setDrawColor(8, 127, 120);
        pdf.setLineWidth(0.6);
        pdf.line(margin, y + 8, pageWidth - margin, y + 8);
        y += 13;
      };

      const drawTable = (headers, rows, columnWidths, rightAlignedColumns = []) => {
        const lineHeight = 4.5;
        const cellPadding = 3;
        const drawTableHeader = () => {
          pdf.setFillColor(232, 244, 242);
          pdf.rect(margin, y, contentWidth, 9, "F");
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(8);
          pdf.setTextColor(47, 76, 81);
          let x = margin;
          headers.forEach((header, index) => {
            const align = rightAlignedColumns.includes(index) ? "right" : "left";
            const textX = align === "right" ? x + columnWidths[index] - cellPadding : x + cellPadding;
            pdf.text(String(header), textX, y + 6, { align });
            x += columnWidths[index];
          });
          y += 9;
        };

        drawTableHeader();
        rows.forEach((row, rowIndex) => {
          const wrappedCells = row.map((value, index) => pdf.splitTextToSize(
            String(value ?? ""),
            columnWidths[index] - cellPadding * 2
          ));
          const rowHeight = Math.max(8, ...wrappedCells.map(lines => lines.length * lineHeight + 3));
          ensureSpace(rowHeight + 9);
          if (y === 18) drawTableHeader();

          if (rowIndex % 2 === 1) {
            pdf.setFillColor(248, 250, 250);
            pdf.rect(margin, y, contentWidth, rowHeight, "F");
          }

          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(8);
          pdf.setTextColor(55, 71, 79);
          let x = margin;
          wrappedCells.forEach((lines, index) => {
            const align = rightAlignedColumns.includes(index) ? "right" : "left";
            const textX = align === "right" ? x + columnWidths[index] - cellPadding : x + cellPadding;
            pdf.text(lines, textX, y + 5, { align, lineHeightFactor: 1.2 });
            x += columnWidths[index];
          });
          pdf.setDrawColor(228, 235, 234);
          pdf.setLineWidth(0.2);
          pdf.line(margin, y + rowHeight, pageWidth - margin, y + rowHeight);
          y += rowHeight;
        });
        y += 7;
      };

      pdf.setFillColor(18, 60, 71);
      pdf.rect(0, 0, pageWidth, 43, "F");
      pdf.setFillColor(8, 127, 120);
      pdf.rect(0, 40, pageWidth, 3, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(19);
      pdf.setTextColor(255, 255, 255);
      pdf.text("eLaba | Revenue Report", margin, 18);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      pdf.setTextColor(213, 239, 234);
      pdf.text("Finalized payment summary across all shops", margin, 25);
      pdf.text(`Reporting period: ${formatPdfDate(report.from)} - ${formatPdfDate(report.to)}`, margin, 33);
      pdf.text(`Generated: ${generatedAt.toLocaleString("en-PH")}`, pageWidth - margin, 33, { align: "right" });

      const metrics = [
        ["TOTAL REVENUE", formatPdfCurrency(summary.totalRevenue)],
        ["PAID PAYMENTS", String(summary.paidPayments || 0)],
        ["COMPLETED BOOKINGS", String(summary.completedBookings || 0)],
        ["AVERAGE PAYMENT", formatPdfCurrency(summary.averagePayment)],
      ];
      const metricGap = 4;
      const metricWidth = (contentWidth - metricGap * 3) / 4;
      y = 51;
      metrics.forEach(([label, value], index) => {
        const x = margin + index * (metricWidth + metricGap);
        pdf.setFillColor(246, 249, 249);
        pdf.setDrawColor(226, 236, 235);
        pdf.roundedRect(x, y, metricWidth, 21, 2, 2, "FD");
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(6.5);
        pdf.setTextColor(103, 121, 125);
        pdf.text(label, x + 3, y + 7);
        pdf.setFontSize(9);
        pdf.setTextColor(18, 60, 71);
        pdf.text(value, x + 3, y + 15);
      });
      y = 80;

      drawSectionTitle("Daily Revenue");
      drawTable(
        ["Date", "Payments", "Revenue"],
        daily.length
          ? daily.map(item => [formatPdfDate(item.date), String(item.payments || 0), formatPdfCurrency(item.revenue)])
          : [["No finalized revenue in this period.", "", ""]],
        [contentWidth * 0.48, contentWidth * 0.22, contentWidth * 0.30],
        [1, 2]
      );

      drawSectionTitle("Revenue by Payment Method");
      const paymentMethods = report.paymentMethods || [];
      drawTable(
        ["Payment method", "Payments", "Revenue"],
        paymentMethods.length
          ? paymentMethods.map(item => [item.method || "Other", String(item.payments || 0), formatPdfCurrency(item.revenue)])
          : [["No payment data available.", "", ""]],
        [contentWidth * 0.48, contentWidth * 0.22, contentWidth * 0.30],
        [1, 2]
      );

      drawSectionTitle("Top Performing Shops");
      const topShops = report.topShops || [];
      drawTable(
        ["#", "Shop", "Payments", "Revenue"],
        topShops.length
          ? topShops.map((shop, index) => [
            String(index + 1),
            shop.shopName || "Unnamed shop",
            String(shop.payments || 0),
            formatPdfCurrency(shop.revenue),
          ])
          : [["", "No shop revenue data available.", "", ""]],
        [contentWidth * 0.08, contentWidth * 0.44, contentWidth * 0.20, contentWidth * 0.28],
        [0, 2, 3]
      );

      const pageCount = pdf.getNumberOfPages();
      for (let page = 1; page <= pageCount; page += 1) {
        pdf.setPage(page);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.setTextColor(125, 139, 142);
        pdf.text("eLaba Revenue Report", margin, pageHeight - 8);
        pdf.text(`Page ${page} of ${pageCount}`, pageWidth - margin, pageHeight - 8, { align: "right" });
      }

      pdf.save(`revenue-report_${report.from}_${report.to}.pdf`);
    } catch (exportError) {
      console.error("Error exporting revenue report:", exportError);
      setError("Unable to export the revenue report. Please try again.");
    }
  };

  return (
    <Layout>
      <div className="analytics-page">
        <header className="analytics-header">
          <div>
            <span className="analytics-eyebrow">Financial overview</span>
            <h1>Revenue Analytics</h1>
            <p>Track finalized payments across every laundry shop.</p>
          </div>
          <div className="analytics-header-actions">
            <button
              type="button"
              className="analytics-export-button"
              onClick={exportRevenueReport}
              disabled={!report || loading}
            >
              Export PDF
            </button>
            <div className="analytics-header-mark">₱</div>
          </div>
        </header>

        <form className="analytics-filters" onSubmit={(event) => { event.preventDefault(); loadReport(); }}>
          <label>From <input type="date" value={from} max={to} onChange={(event) => setFrom(event.target.value)} /></label>
          <label>To <input type="date" value={to} min={from} max={initialTo} onChange={(event) => setTo(event.target.value)} /></label>
          <button type="submit" disabled={loading}>{loading ? "Loading..." : "Apply dates"}</button>
          <span className="analytics-range">{report ? `${report.from} to ${report.to}` : "Select a reporting period"}</span>
        </form>

        {error && <div className="analytics-error" role="alert">{error}</div>}

        {loading && !report ? (
          <div className="analytics-state">Loading revenue data...</div>
        ) : !report ? (
          <div className="analytics-state">
            <div>{error || "Unable to load revenue analytics."}</div>
            <button type="button" onClick={loadReport} disabled={loading}>Try again</button>
          </div>
        ) : (
          <>
            <section className="analytics-kpis">
              <article><span>Total revenue</span><strong>{currency(summary.totalRevenue)}</strong><small>Finalized payments only</small></article>
              <article><span>Paid payments</span><strong>{summary.paidPayments || 0}</strong><small>Transaction-linked records</small></article>
              <article><span>Completed bookings</span><strong>{summary.completedBookings || 0}</strong><small>Unique paid bookings</small></article>
              <article><span>Average payment</span><strong>{currency(summary.averagePayment)}</strong><small>Per finalized payment</small></article>
            </section>

            <section className="analytics-grid">
              <article className="analytics-panel trend-panel">
                <div className="panel-heading"><div><h2>Revenue trend</h2><p>Daily revenue within the selected period</p></div><strong>{daily.length} days</strong></div>
                {daily.length ? <div className="revenue-chart" aria-label="Daily revenue chart">{daily.map(item => {
                  const label = shortDate(item.date);
                  return (
                    <div className="chart-column" key={item.date || `${label}-${Math.random()}`} title={`${label}: ${currency(item.revenue)}`}>
                      <div className="chart-bar" style={{ height: `${Math.max((Number(item.revenue) / maxRevenue) * 100, 4)}%` }} />
                      <span>{label}</span>
                    </div>
                  );
                })}</div> : <div className="empty-state">No finalized revenue in this period.</div>}
              </article>

              <article className="analytics-panel">
                <div className="panel-heading"><div><h2>Payment methods</h2><p>Revenue share by method</p></div></div>
                {(report?.paymentMethods || []).length ? <div className="breakdown-list">{report.paymentMethods.map(item => <div className="breakdown-row" key={item.method}><div><strong>{item.method}</strong><small>{item.payments} payment{item.payments === 1 ? "" : "s"}</small></div><strong>{currency(item.revenue)}</strong></div>)}</div> : <div className="empty-state">No payment data available.</div>}
              </article>
            </section>

            <section className="analytics-panel shops-panel">
              <div className="panel-heading"><div><h2>Top performing shops</h2><p>Ranked by finalized revenue</p></div></div>
              {(report?.topShops || []).length ? <div className="shop-table"><div className="shop-table-head"><span>Shop</span><span>Payments</span><span>Revenue</span></div>{report.topShops.map((shop, index) => <div className="shop-table-row" key={shop.shopId || shop.shopName}><span><b>{String(index + 1).padStart(2, "0")}</b>{shop.shopName}</span><span>{shop.payments}</span><strong>{currency(shop.revenue)}</strong></div>)}</div> : <div className="empty-state">No shop revenue data available.</div>}
            </section>
          </>
        )}
      </div>
    </Layout>
  );
}
